export const SEARCH_API_PATH = '/api/v1/search';
export const SEARCH_UNAVAILABLE_STATUS = 503;

export const SEARCH_API_PARAMS = {
  TERM: 'q',
  PAGE_NUMBER: 'page[number]',
  PAGE_SIZE: 'page[size]',
  LANGUAGE_FILTER: 'filter[language]',
  TRAINING_TYPE_FILTER: 'filter[training_type]',
} as const;

export const SEARCH_FILTER_SEPARATOR = ',';

export const SEARCH_QUERY_PARAMS = {
  TERM: 'q',
  TRAINING_TYPE: 'trainingType',
  LANGUAGE: 'language',
  PAGE: 'page',
  PAGE_SIZE: 'pageSize',
} as const;

export const SEARCH_VISIBLE_TAG_LIMIT = 6;

export const DEFAULT_SEARCH_PAGE = 1;
export const DEFAULT_SEARCH_PAGE_SIZE = 20;

export const SYNTHETIC_NEXT_PAGE_ITEM = 1;

export const SEARCH_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

export type SearchPageSize = (typeof SEARCH_PAGE_SIZE_OPTIONS)[number];

const SEARCH_PAGE_SIZE_VALUES: readonly number[] = SEARCH_PAGE_SIZE_OPTIONS;

function isSearchPageSize(value: number): value is SearchPageSize {
  return SEARCH_PAGE_SIZE_VALUES.includes(value);
}

export function toSearchPageSize(value: number): SearchPageSize {
  return isSearchPageSize(value) ? value : DEFAULT_SEARCH_PAGE_SIZE;
}

export function parseSearchPage(value: string | null): number {
  const page = Number(value);

  return Number.isInteger(page) && page >= DEFAULT_SEARCH_PAGE
    ? page
    : DEFAULT_SEARCH_PAGE;
}

export function parseSearchPageSize(value: string | null): SearchPageSize {
  return toSearchPageSize(Number(value));
}

// JSON:API `data[].type` values returned by the search endpoint.
export const SEARCH_RESOURCE_TYPES = {
  COURSE: 'courses',
  LEARNING_PATH: 'learning_paths',
  ILT_COURSE: 'ilt_courses',
  SCHEDULE: 'schedules',
} as const;

export type SearchResourceType =
  (typeof SEARCH_RESOURCE_TYPES)[keyof typeof SEARCH_RESOURCE_TYPES];

const SEARCH_RESOURCE_TYPE_VALUES: readonly string[] = Object.values(
  SEARCH_RESOURCE_TYPES,
);

export function isSearchResourceType(
  value: string,
): value is SearchResourceType {
  return SEARCH_RESOURCE_TYPE_VALUES.includes(value);
}

// `filter[training_type]` values accepted by the search endpoint.
export const SEARCH_TRAINING_TYPE_FILTERS = {
  COURSE: 'course',
  LEARNING_PATH: 'learning_path',
  ILT_COURSE: 'ilt_course',
  SCHEDULE: 'schedule',
} as const;

export type SearchTrainingTypeFilter =
  (typeof SEARCH_TRAINING_TYPE_FILTERS)[keyof typeof SEARCH_TRAINING_TYPE_FILTERS];

// Per-record policy predicate: admin/coach, or author/editor of that very record.
export const SEARCH_EDIT_PERMISSIONS = {
  UPDATE: 'update',
} as const;

export type SearchEditPermission =
  (typeof SEARCH_EDIT_PERMISSIONS)[keyof typeof SEARCH_EDIT_PERMISSIONS];

export interface SearchResultTypeConfig {
  readonly filter: SearchTrainingTypeFilter;
  readonly icon: string;
  readonly isSvgIcon: boolean;
  readonly labelKey: string;
  readonly editPermission: SearchEditPermission;
  readonly overviewPath: (id: string) => string;
  readonly editPath: (id: string) => string;
}

export const SEARCH_RESULT_TYPE_CONFIG: Record<
  SearchResourceType,
  SearchResultTypeConfig
> = {
  [SEARCH_RESOURCE_TYPES.COURSE]: {
    filter: SEARCH_TRAINING_TYPE_FILTERS.COURSE,
    icon: 'school',
    isSvgIcon: false,
    labelKey: 'filters.course',
    editPermission: SEARCH_EDIT_PERMISSIONS.UPDATE,
    overviewPath: id => `/courses/${id}/overview`,
    editPath: id => `/courses/${id}/content`,
  },
  [SEARCH_RESOURCE_TYPES.LEARNING_PATH]: {
    filter: SEARCH_TRAINING_TYPE_FILTERS.LEARNING_PATH,
    icon: 'route',
    isSvgIcon: false,
    labelKey: 'filters.learningPath',
    editPermission: SEARCH_EDIT_PERMISSIONS.UPDATE,
    overviewPath: id => `/learning-paths/${id}/overview`,
    editPath: id => `/learning-paths/${id}/content`,
  },
  [SEARCH_RESOURCE_TYPES.ILT_COURSE]: {
    filter: SEARCH_TRAINING_TYPE_FILTERS.ILT_COURSE,
    icon: 'live_training_teacher',
    isSvgIcon: true,
    labelKey: 'filters.iltCourse',
    editPermission: SEARCH_EDIT_PERMISSIONS.UPDATE,
    overviewPath: id => `/ilt-courses/${id}/overview`,
    editPath: id => `/ilt-courses/${id}/content`,
  },
  // Schedules have no per-training learner view, so the talent calendar is the user link.
  [SEARCH_RESOURCE_TYPES.SCHEDULE]: {
    filter: SEARCH_TRAINING_TYPE_FILTERS.SCHEDULE,
    icon: 'date_range',
    isSvgIcon: false,
    labelKey: 'filters.trainingSchedule',
    editPermission: SEARCH_EDIT_PERMISSIONS.UPDATE,
    overviewPath: () => '/training-schedules/my',
    editPath: id => `/training-schedules/${id}/content`,
  },
};

export const SEARCH_TRAINING_TYPE_OPTIONS: readonly SearchResultTypeConfig[] = [
  SEARCH_RESOURCE_TYPES.COURSE,
  SEARCH_RESOURCE_TYPES.ILT_COURSE,
  SEARCH_RESOURCE_TYPES.LEARNING_PATH,
  SEARCH_RESOURCE_TYPES.SCHEDULE,
].map(type => SEARCH_RESULT_TYPE_CONFIG[type]);
